// =======================================================
// CodeFrame — sparse monospace fragments composed across the ribbon.
// Editorial layout: IDs, paren-strings, hex, timecodes appear in cells,
// some flicker on a deterministic cadence so it loops seamlessly.
// =======================================================

import * as THREE from 'three';
import { Generator } from './Generator.js';

const DEFAULT_FRAGMENTS = [
  '00:00:01:44', '0xAE17', '0xC0FFEE', 'NULL(0)', 'BUF<7F>',
  '(EDI)', '[##]', '~~', '...', '???', 'OK.', '(--)',
  'SEED:0x1F', 'MSG[42]', 'IDX/19', 'CH.404', 'SIG=NULL',
  'PAYLOAD[256]', 'STATE/01', 'TMP.~/dev/0', 'REF#000',
  'PROC=MAIN', 'NODE.SRC', 'KEY=K3', 'V-4',
];

export class CodeFrame extends Generator {
  constructor() {
    super({
      name: 'Code Frame',
      category: 'ambient',
      schema: {
        sources:      { default: DEFAULT_FRAGMENTS.join(' | '), label: 'Sources (|)' },
        accent:       { default: '#ff1f1f', type: 'color', label: 'Accent' },
        fg:           { default: '#e9e6ff', type: 'color', label: 'Foreground' },
        bg:           { default: '#000000', type: 'color', label: 'Background' },
        density:      { default: 0.35, min: 0.02, max: 1.0, step: 0.01, label: 'Density' },
        accentChance: { default: 0.18, min: 0, max: 1, step: 0.01, label: 'Accent chance' },
        fontPx:       { default: 22, min: 8, max: 96, step: 1, label: 'Font px' },
        margin:       { default: 60, min: 0, max: 400, step: 5, label: 'Margin px' },
        jitter:       { default: 0.4, min: 0, max: 1.2, step: 0.02, label: 'Position jitter' },
        cellPad:      { default: 1.2, min: 0.6, max: 3.0, step: 0.05, label: 'Cell pad X' },
        churn:        { default: 4, min: 0, max: 64, step: 1, label: 'Churn steps/loop' },
        churnFrac:    { default: 0.18, min: 0, max: 1, step: 0.01, label: 'Churn fraction' },
        weight: {
          default: '400', type: 'select', label: 'Weight',
          options: [['400','Regular'], ['600','Semibold'], ['700','Bold']],
        },
        alignLeft:    { default: true, type: 'bool', label: 'Left-align row blocks' },
      },
    });
    this._sourceList = DEFAULT_FRAGMENTS.slice();
    this._fragments = [];
    this._layoutDirty = true;
    this._lastChurnStep = -1;
  }

  onEnter(scene, engine) {
    this.engine = engine;

    this.off = document.createElement('canvas');
    this.off.width = engine.width;
    this.off.height = engine.height;
    this.offCtx = this.off.getContext('2d', { alpha: false });

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this._updateMeshScale();
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);

    this._parseSources();
    this._regenerateLayout();
    this._redraw();
  }

  onResize(w, h) {
    if (!this.off) return;
    this.off.width = w;
    this.off.height = h;
    this._updateMeshScale();
    this._layoutDirty = true;
  }

  _updateMeshScale() {
    if (!this.mesh || !this.engine) return;
    const aspect = this.engine.width / this.engine.height;
    this.mesh.scale.set(aspect, 1, 1);
  }

  onParamChange(key) {
    if (key === 'sources') {
      this._parseSources();
      this._layoutDirty = true;
      return;
    }
    if (key === 'density' || key === 'fontPx' || key === 'margin'
        || key === 'jitter' || key === 'cellPad' || key === 'weight'
        || key === 'alignLeft') {
      this._layoutDirty = true;
      return;
    }
    this._redraw();
  }

  _parseSources() {
    const raw = String(this.v('sources') || '').split('|').map(s => s.trim()).filter(Boolean);
    this._sourceList = raw.length ? raw : DEFAULT_FRAGMENTS.slice();
  }

  // Cheap deterministic hash → [0,1)
  _hash(a, b = 0, c = 0) {
    let x = ((a | 0) ^ ((b | 0) * 0x9e3779b9) ^ ((c | 0) * 0x85ebca6b) ^ (this.seed | 0)) >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x7feb352d) >>> 0;
    x ^= x >>> 15;
    x = Math.imul(x, 0x846ca68b) >>> 0;
    x ^= x >>> 16;
    return (x >>> 0) / 4294967296;
  }

  _regenerateLayout() {
    const w = this.off.width;
    const h = this.off.height;
    const margin = this.v('margin');
    const fontPx = this.v('fontPx');
    const density = this.v('density');
    const jitter = this.v('jitter');
    const cellPad = this.v('cellPad');

    // Approx glyph metrics for a monospace at fontPx — used only for cell sizing.
    const charW = fontPx * 0.62;
    const lineH = fontPx * 1.6;

    // We size columns to fit the *longest* source, padded.
    let maxChars = 1;
    for (const s of this._sourceList) maxChars = Math.max(maxChars, s.length);
    const cellW = Math.max(charW * 2, charW * maxChars * cellPad);
    const cellH = Math.max(lineH, lineH);

    const innerW = Math.max(1, w - margin * 2);
    const innerH = Math.max(1, h - margin * 2);
    const cols = Math.max(1, Math.floor(innerW / cellW));
    const rows = Math.max(1, Math.floor(innerH / cellH));

    const slotW = innerW / cols;
    const slotH = innerH / rows;

    const total = cols * rows;
    const used = Math.max(1, Math.floor(total * density));

    // Deterministic selection of "used" cells from the [0..total) range.
    const slots = new Array(total);
    for (let i = 0; i < total; i++) slots[i] = i;
    // Fisher–Yates with hash randomness.
    for (let i = total - 1; i > 0; i--) {
      const j = Math.floor(this._hash(i * 11, 2) * (i + 1));
      const tmp = slots[i]; slots[i] = slots[j]; slots[j] = tmp;
    }
    const chosen = slots.slice(0, used);

    this._fragments = [];
    for (let k = 0; k < chosen.length; k++) {
      const idx = chosen[k];
      const cx = idx % cols;
      const cy = Math.floor(idx / cols);
      const baseX = margin + cx * slotW + (this.v('alignLeft') ? 0 : slotW * 0.5);
      const baseY = margin + cy * slotH + slotH * 0.5;
      const jx = (this._hash(idx, 5) - 0.5) * 2 * jitter * slotW * 0.45;
      const jy = (this._hash(idx, 9) - 0.5) * 2 * jitter * slotH * 0.35;
      this._fragments.push({
        idx,
        x: baseX + jx,
        y: baseY + jy,
        // Pre-baked per-slot identity; the actual text is resolved per churn step.
        slotSeed: idx,
      });
    }
    this._layoutDirty = false;
    this._lastChurnStep = -1;
  }

  _resolveText(slotSeed, churnStep) {
    const list = this._sourceList;
    if (!list.length) return '';
    const r = this._hash(slotSeed, churnStep + 1, 31);
    return list[Math.floor(r * list.length)];
  }

  _resolveAccent(slotSeed, churnStep) {
    return this._hash(slotSeed * 3 + 17, churnStep + 1, 91) < this.v('accentChance');
  }

  _redraw() {
    if (!this.offCtx) return;
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const fontPx = this.v('fontPx');

    ctx.fillStyle = this.v('bg');
    ctx.fillRect(0, 0, w, h);

    ctx.font = `${this.v('weight')} ${fontPx}px 'JetBrains Mono', ui-monospace, Consolas, monospace`;
    ctx.textAlign = this.v('alignLeft') ? 'left' : 'center';
    ctx.textBaseline = 'middle';

    const accentColor = this.v('accent');
    const fgColor = this.v('fg');
    const churnStep = Math.max(0, this._lastChurnStep);

    for (const f of this._fragments) {
      const accent = this._resolveAccent(f.slotSeed, churnStep);
      ctx.fillStyle = accent ? accentColor : fgColor;
      const txt = this._resolveText(f.slotSeed, churnStep);
      ctx.fillText(txt, f.x, f.y);
    }
    this.tex.needsUpdate = true;
  }

  update(dt, time, engine) {
    if (this._layoutDirty) {
      this._regenerateLayout();
      this._redraw();
    }
    const churnPerLoop = Math.max(0, Math.floor(this.v('churn')));
    if (churnPerLoop === 0) {
      if (this._lastChurnStep !== 0) {
        this._lastChurnStep = 0;
        this._redraw();
      }
      return;
    }
    const loop = engine.getLoopInfo(time);
    const step = Math.floor(loop.phase * churnPerLoop);
    if (step !== this._lastChurnStep) {
      this._lastChurnStep = step;
      // Optionally only refresh a fraction of fragments. Determinism: each
      // fragment decides per (slotSeed, step) whether it's part of this churn.
      const frac = this.v('churnFrac');
      if (frac >= 0.999) {
        this._redraw();
      } else {
        // Partial refresh — we still redraw the whole canvas (cheaper than
        // tracking dirty regions), but the *content* of unaffected fragments
        // stays pinned to the previous churnStep.
        // Implemented by giving each fragment its own effective step.
        for (const f of this._fragments) {
          if (f._effectiveStep === undefined) f._effectiveStep = 0;
          const r = this._hash(f.slotSeed, step + 1, 53);
          if (r < frac) f._effectiveStep = step;
        }
        this._redrawPartial();
      }
    }
  }

  _redrawPartial() {
    if (!this.offCtx) return;
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const fontPx = this.v('fontPx');

    ctx.fillStyle = this.v('bg');
    ctx.fillRect(0, 0, w, h);

    ctx.font = `${this.v('weight')} ${fontPx}px 'JetBrains Mono', ui-monospace, Consolas, monospace`;
    ctx.textAlign = this.v('alignLeft') ? 'left' : 'center';
    ctx.textBaseline = 'middle';

    const accentColor = this.v('accent');
    const fgColor = this.v('fg');

    for (const f of this._fragments) {
      const step = f._effectiveStep ?? 0;
      const accent = this._resolveAccent(f.slotSeed, step);
      ctx.fillStyle = accent ? accentColor : fgColor;
      ctx.fillText(this._resolveText(f.slotSeed, step), f.x, f.y);
    }
    this.tex.needsUpdate = true;
  }

  reseed() {
    this._layoutDirty = true;
  }
}
